export type PaymentRequestSendStatus =
  | 'pending'
  | 'sent'
  | 'in_collection'
  | 'resolved'
  | 'send_failed'
  | 'disabled'
  | 'deleted'
  | 'pac_native';

export type PaymentRequestRouting = 'pac' | 'lups';

export type PaymentRequestStatusFilter = PaymentRequestSendStatus | 'all';
export type PaymentRequestRoutingFilter = 'all' | 'pac' | 'lups';

export interface PaymentRequestListItem {
  id: string;
  advanceRequestId: string;
  installmentDetailId: string;
  lupsRequestId: string | null;
  connectorId: string | null;
  sendStatus: PaymentRequestSendStatus;
  routing: PaymentRequestRouting;
  mechanism: 'PAC' | null;
  lupsPaymentMethod: string | null;
  pacActivatedAt: string | null;
  monto: string;
  fechaObjetivo: string;
  abandonmentDate: string;
  pacActive: boolean;
  pacMandateId: string | null;
  pacRetryCount: number;
  pacLastFailureCode: string | null;
  collectedAt: string | null;
  abandonedAt: string | null;
  disabledAt: string | null;
  disableReason: string | null;
  deletedAt: string | null;
  deleteResponseStatus: number | null;
  sendAttempts: number;
  collectionAttempts: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentRequestDetail extends PaymentRequestListItem {
  company: { id: string; taxId: string | null; businessName: string | null } | null;
  installment: { number: number; dueAt: string | null; paymentAmount: string | null } | null;
  events: { idEvento: string; eventType: string; processedAt: string | null; timestampEvento: string }[];
}

export interface PaymentRequestsListResult {
  page: number;
  pageSize: number;
  total: number;
  items: PaymentRequestListItem[];
}

export interface PaymentRequestStats {
  bySendStatus: { sendStatus: PaymentRequestSendStatus; count: number }[];
  routingActive: { pac: number; lups: number };
  collected24h: number;
  abandoned24h: number;
  sendFailed: number;
  abandonmentsThisWeek: number;
}

export const SEND_STATUS_LABELS: Record<PaymentRequestSendStatus, string> = {
  pending: 'Pendiente',
  sent: 'Enviado',
  in_collection: 'En cobro',
  resolved: 'Cobrado',
  send_failed: 'Envío fallido',
  disabled: 'Deshabilitado',
  deleted: 'Eliminado',
  // El sendStatus interno `pac_native` es equivalente a "en cobro" con canal
  // PAC en lugar de Lups. La columna Canal ya distingue PAC/Lups, así que acá
  // sólo importa la fase del ciclo (el admin no debe ver "nativo" como término).
  pac_native: 'En cobro',
};

export const SEND_STATUS_TONE: Record<PaymentRequestSendStatus, { dot: string; text: string; bg: string; ring: string }> = {
  pending: { dot: 'bg-slate-400', text: 'text-slate-700 dark:text-slate-300', bg: 'bg-slate-100 dark:bg-slate-800', ring: 'ring-slate-200 dark:ring-slate-700' },
  sent: { dot: 'bg-blue-500', text: 'text-blue-700 dark:text-blue-300', bg: 'bg-blue-50 dark:bg-blue-950/30', ring: 'ring-blue-200 dark:ring-blue-900/50' },
  in_collection: { dot: 'bg-cyan-500', text: 'text-cyan-700 dark:text-cyan-300', bg: 'bg-cyan-50 dark:bg-cyan-950/30', ring: 'ring-cyan-200 dark:ring-cyan-900/50' },
  resolved: { dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-950/30', ring: 'ring-emerald-200 dark:ring-emerald-900/50' },
  send_failed: { dot: 'bg-rose-500', text: 'text-rose-700 dark:text-rose-300', bg: 'bg-rose-50 dark:bg-rose-950/30', ring: 'ring-rose-200 dark:ring-rose-900/50' },
  disabled: { dot: 'bg-slate-400', text: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-50 dark:bg-slate-800/40', ring: 'ring-slate-200 dark:ring-slate-700' },
  deleted: { dot: 'bg-slate-400', text: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-50 dark:bg-slate-800/40', ring: 'ring-slate-200 dark:ring-slate-700' },
  // pac_native usa el mismo tono que in_collection: la diferencia con Lups la
  // muestra la columna Canal, no el sendStatus.
  pac_native: { dot: 'bg-cyan-500', text: 'text-cyan-700 dark:text-cyan-300', bg: 'bg-cyan-50 dark:bg-cyan-950/30', ring: 'ring-cyan-200 dark:ring-cyan-900/50' },
};

export const ROUTING_LABELS: Record<PaymentRequestRouting, string> = {
  pac: 'PAC',
  lups: 'Lups',
};
